// Q12 S5 — drawer click-flow handlers extracted from NotificationsDrawer
// to keep that component under the 80-line cap. Returns the four handlers
// the drawer wires to NotificationRow plus the optimistic-dismiss
// filtering predicate.
//
// Carer-parity S3b: switched the network helper from a direct apiFetch
// import to useFetcher() so the carer subtree's PATCH /:id/read,
// PATCH /:id/dismiss, and PATCH /read-all calls inherit the
// silent-refresh-on-401 path via carerFetch. Without this, a token
// expiring after the drawer was opened (carer 30-min Redis inactivity TTL
// vs 5–15 min drawer interaction) would 401 the dismiss request and
// strand the optimistic UI in the rolled-back state.
//
// Both subtrees query + invalidate on the same `["notifications"]` key.
// The carer-side feed lives at ["carer-notifications"] in CarerLayout, so
// carer-side invalidation also targets the carer key — done explicitly
// below since the drawer doesn't know which subtree mounted it.

import { useState } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useNavigate, type NavigateFunction } from "react-router-dom";
import { toast } from "sonner";
import { useFetcher, type FetcherFn } from "./useFetcher.js";
import { buildUrl, shouldOpenInNewTab } from "../lib/notification-link.js";
import type { Notification } from "../components/NotificationsDrawer.js";

interface Actions {
  visible: Notification[];
  handleActivate: (id: string) => void;
  handleMarkRead: (id: string) => void;
  handleDismiss: (id: string) => void;
  markAllRead: () => Promise<void>;
}

const NOTIFICATION_QUERY_KEYS = [
  ["notifications"],
  ["carer-notifications"],
] as const;

function invalidateBoth(qc: QueryClient): void {
  for (const key of NOTIFICATION_QUERY_KEYS) {
    void qc.invalidateQueries({ queryKey: key });
  }
}

function fireAndForgetMarkRead(id: string, fetcher: FetcherFn, qc: QueryClient): void {
  void fetcher(`/notifications/${id}/read`, { method: "PATCH" })
    .then(() => invalidateBoth(qc));
}

function dispatchActivate(item: Notification, navigate: NavigateFunction, onClose: () => void): void {
  const url = buildUrl(item.link);
  if (!url) return;                                    // D13a: link-less = mark-read only above
  if (shouldOpenInNewTab(item.link)) {
    window.open(url, "_blank", "noopener,noreferrer"); // External: drawer stays open
    return;
  }
  navigate(url);                                       // Internal: navigate + close (D11b/D12a)
  onClose();
}

export function useNotificationActions(items: Notification[], onClose: () => void): Actions {
  const fetcher: FetcherFn = useFetcher();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const visible = items.filter((n) => !dismissed.has(n._id));
  const handleMarkRead = (id: string): void => fireAndForgetMarkRead(id, fetcher, qc);

  function handleActivate(id: string): void {
    const item = items.find((n) => n._id === id);
    if (!item) return;
    handleMarkRead(id);
    dispatchActivate(item, navigate, onClose);
  }

  function handleDismiss(id: string): void {
    setDismissed((prev) => new Set(prev).add(id));
    void fetcher(`/notifications/${id}/dismiss`, { method: "PATCH" })
      .then(() => invalidateBoth(qc))
      .catch(() => {
        setDismissed((prev) => { const s = new Set(prev); s.delete(id); return s; });
        toast.error("Could not dismiss notification — please try again.");
      });
  }

  async function markAllRead(): Promise<void> {
    await fetcher("/notifications/read-all", { method: "PATCH" });
    invalidateBoth(qc);
  }

  return { visible, handleActivate, handleMarkRead, handleDismiss, markAllRead };
}
